import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PawPrint from '../components/PawPrint';
import { ArrowLeft } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
      toast.success('If email exists, reset link sent');
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#1B4332]" data-testid="forgot-password-page">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#BFDB38]/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#FF6B6B]/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>

      <div className="w-full max-w-md relative z-10">
        <div className="flex items-center justify-center gap-2 mb-8">
          <PawPrint className="w-10 h-10" color="#BFDB38" />
          <span className="text-3xl font-black text-white" style={{ fontFamily: 'Nunito, sans-serif' }}>PurrCheck</span>
        </div>

        <div className="bg-white border-2 border-[#1B4332]/10 rounded-3xl p-8 shadow-2xl" data-testid="forgot-password-card">
          <h2 className="text-2xl font-black text-[#1B4332] text-center mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Forgot Password?
          </h2>
          <p className="text-center text-[#1B4332]/50 text-sm mb-6">Enter your email to receive a reset link</p>

          {sent ? (
            <div className="text-center space-y-4">
              <div className="bg-[#BFDB38]/10 rounded-2xl p-6">
                <p className="text-[#1B4332] text-sm" data-testid="forgot-success-message">
                  If an account exists for <span className="font-bold text-[#FF6B6B]">{email}</span>, a password reset link has been sent.
                </p>
              </div>
              <Link to="/login">
                <Button className="w-full rounded-full font-bold h-12 bg-[#1B4332] hover:bg-[#1B4332]/90 text-white hover:-translate-y-0.5 transition-all" data-testid="forgot-back-btn">Back to Login</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#FF6B6B]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-white border-2 border-[#1B4332]/10 rounded-xl focus:border-[#1B4332] h-12"
                  required
                  data-testid="forgot-email-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full rounded-full font-bold h-12 bg-[#1B4332] hover:bg-[#1B4332]/90 text-white text-base hover:-translate-y-0.5 transition-all"
                disabled={loading}
                data-testid="forgot-submit-btn"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>

              <p className="text-center text-sm text-[#1B4332]/50 mt-4">
                <Link to="/login" className="inline-flex items-center gap-1.5 text-[#FF6B6B] font-bold hover:underline">
                  <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /> Back to Login
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
